import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "./AuthContext";
import { useRealtimeConnection, useRealtimeEvent, useRealtimeReady } from "./RealtimeContext";
import { useAlphaAccess } from "../hooks/useAlphaAccess";
import { useWindowFocusRefresh } from "../hooks/useWindowFocusRefresh";
import notificationInboxService from "../services/notificationInboxService";
import type { RealtimeConnectionState } from "../types/realtime";

/**
 * First subscriber of the realtime layer (PWA-2689 Section 3.5): owns the WEB
 * unread count and the notification refresh policy. It never owns a connection.
 *
 * - Count source: the list endpoint's whole-surface `unreadCount` (no count API).
 * - `inbox-changed` bursts coalesce into one refresh 500 ms after the first event.
 * - Fallback: every 120 s while the stream is not open, every 15 min while it is;
 *   both pause while the tab is hidden and run once on return after 30 s.
 */

export const NOTIFICATION_COALESCE_MS = 500;
export const NOTIFICATION_FALLBACK_INTERVAL_MS = 120_000;
export const NOTIFICATION_SAFETY_INTERVAL_MS = 15 * 60_000;

export interface NotificationsContextValue {
  enabled: boolean;
  unreadCount: number | null;
  connection: RealtimeConnectionState;
  /** Increments whenever pages should refetch their current view. */
  invalidationVersion: number;
  refreshUnreadCount: () => Promise<void>;
  applyUnreadCount: (count: number) => void;
}

const INERT_VALUE: NotificationsContextValue = {
  enabled: false,
  unreadCount: null,
  connection: "idle",
  invalidationVersion: 0,
  refreshUnreadCount: async () => undefined,
  applyUnreadCount: () => undefined,
};

const NotificationsContext = createContext<NotificationsContextValue>(INERT_VALUE);

export const NotificationsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { actorUserId } = useAuth();
  const realtime = useRealtimeConnection();
  const hasAlphaAccess = useAlphaAccess();
  const enabled = realtime.enabled && hasAlphaAccess;

  const [unreadCount, setUnreadCount] = useState<number | null>(null);
  const [invalidationVersion, setInvalidationVersion] = useState(0);
  const requestRef = useRef(0);
  const actorRef = useRef(actorUserId);
  actorRef.current = actorUserId;
  const inFlightRef = useRef(false);
  const dirtyRef = useRef(false);
  const coalesceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastRefreshRef = useRef(0);
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;

  const refreshUnreadCount = useCallback(async (): Promise<void> => {
    if (!enabledRef.current) return;
    if (inFlightRef.current) {
      dirtyRef.current = true;
      return;
    }
    inFlightRef.current = true;
    const reqId = ++requestRef.current;
    const actorAtStart = actorRef.current;
    try {
      const page = await notificationInboxService.list({ page: 1, pageSize: 1 });
      if (reqId !== requestRef.current || actorAtStart !== actorRef.current) return;
      lastRefreshRef.current = Date.now();
      setUnreadCount(page.unreadCount);
      setInvalidationVersion(version => version + 1);
    } catch {
      // Keep the previous count; the next tick or event retries. The bell is ambient.
    } finally {
      if (reqId === requestRef.current) {
        inFlightRef.current = false;
        if (dirtyRef.current) {
          dirtyRef.current = false;
          void refreshUnreadCount();
        }
      }
    }
  }, []);

  const scheduleCoalescedRefresh = useCallback(() => {
    if (coalesceTimerRef.current !== null) return;
    coalesceTimerRef.current = setTimeout(() => {
      coalesceTimerRef.current = null;
      void refreshUnreadCount();
    }, NOTIFICATION_COALESCE_MS);
  }, [refreshUnreadCount]);

  useRealtimeReady(() => {
    void refreshUnreadCount();
  });
  useRealtimeEvent("notification.inbox-changed", () => {
    scheduleCoalescedRefresh();
  });

  // Reset on actor change; drop anything in flight for the previous actor.
  useEffect(() => {
    requestRef.current += 1;
    inFlightRef.current = false;
    dirtyRef.current = false;
    setUnreadCount(null);
  }, [actorUserId]);

  // Fallback intervals, paused while hidden.
  useEffect(() => {
    if (!enabled) return undefined;
    const intervalMs = realtime.connection === "open" ? NOTIFICATION_SAFETY_INTERVAL_MS : NOTIFICATION_FALLBACK_INTERVAL_MS;
    if (realtime.connection === "offline") return undefined;
    const tick = () => {
      if (typeof document !== "undefined" && document.visibilityState === "hidden") return;
      void refreshUnreadCount();
    };
    const timer = setInterval(tick, intervalMs);
    return () => clearInterval(timer);
  }, [enabled, realtime.connection, refreshUnreadCount]);

  useWindowFocusRefresh(() => {
    if (!enabledRef.current) return;
    if (Date.now() - lastRefreshRef.current < 30_000) return;
    void refreshUnreadCount();
  });

  useEffect(
    () => () => {
      if (coalesceTimerRef.current !== null) clearTimeout(coalesceTimerRef.current);
    },
    []
  );

  const applyUnreadCount = useCallback((count: number) => {
    lastRefreshRef.current = Date.now();
    setUnreadCount(count);
  }, []);

  const value = useMemo<NotificationsContextValue>(
    () =>
      enabled
        ? { enabled: true, unreadCount, connection: realtime.connection, invalidationVersion, refreshUnreadCount, applyUnreadCount }
        : INERT_VALUE,
    [applyUnreadCount, enabled, invalidationVersion, realtime.connection, refreshUnreadCount, unreadCount]
  );

  return <NotificationsContext.Provider value={value}>{children}</NotificationsContext.Provider>;
};

export const useNotifications = (): NotificationsContextValue => useContext(NotificationsContext);

export default NotificationsContext;
